import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import './Register.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSTITUTIONS = [
  // Presidencia
  'Vicepresidencia de la República',
  // Ministerios
  'Ministerio de Relaciones Exteriores',
  'Ministerio de Gobernación',
  'Ministerio de la Defensa Nacional',
  'Ministerio de Finanzas Públicas',
  'Ministerio de Educación',
  'Ministerio de Salud Pública y Asistencia Social',
  'Ministerio de Trabajo y Previsión Social',
  'Ministerio de Economía',
  'Ministerio de Agricultura, Ganadería y Alimentación',
  'Ministerio de Comunicaciones, Infraestructura y Vivienda',
  'Ministerio de Energía y Minas',
  'Ministerio de Cultura y Deportes',
  'Ministerio de Ambiente y Recursos Naturales',
  'Ministerio de Desarrollo Social',
  // Secretarías
  'Secretaría General de la Presidencia',
  'Secretaría Privada de la Presidencia',
  'Secretaría de Coordinación Ejecutiva de la Presidencia',
  'Secretaría de Comunicación Social de la Presidencia',
  'Secretaría de Inteligencia Estratégica del Estado',
  'Secretaría de Planificación y Programación de la Presidencia',
  'Secretaría de Asuntos Administrativos y de Seguridad',
  'Secretaría Presidencial de la Mujer',
  'Secretaría de Seguridad Social de la Presidencia',
  'Secretaría Ejecutiva de la Comisión Contra las Adicciones y el Tráfico Ilícito de Drogas',
  'Secretaría de Obras Sociales de la Esposa del Presidente',
  'Secretaría Nacional de Ciencia y Tecnología',
  'Secretaría de Seguridad Alimentaria y Nutricional',
  'Secretaría contra la Violencia Sexual, Explotación y Trata de Personas',
  'Secretaría Técnica del Consejo Nacional de Seguridad',
  'Secretaría Nacional de Administración de Bienes de Extinción de Dominio',
  // Autoridades
  'Autoridad para el Manejo Sustentable de la Cuenca del Lago de Atitlán',
  'Autoridad para el Manejo Sustentable de la Cuenca del Lago de Atitlán y su Entorno',
  // Consejos
  'Consejo Nacional de la Juventud',
  'Consejo Nacional de Áreas Protegidas',
  // Oficinas
  'Oficina Nacional de Servicio Civil',
  // Defensorías
  'Defensoría de la Mujer Indígena',
  // Fondos
  'Fondo de Desarrollo Indígena Guatemalteco',
  // Inspectorías
  'Inspectoría General del Sistema Nacional de Seguridad',
  // Institutos
  'Instituto Nacional de Estudios Estratégicos de Seguridad',
  // Comisiones
  'Comisión Presidencial contra la Discriminación y el Racismo Contra los Pueblos Indígenas en Guatemala',
  'Comisión Nacional contra la Corrupción',
  'Comisión Presidencial por la Paz y los Derechos Humanos',
  'Comisión Presidencial de Gobierno Abierto',
  'Comisión Presidencial de Asuntos Municipales',
  // Gobernaciones
  'Gobernación Departamental de Guatemala',
  'Gobernación Departamental de El Progreso',
  'Gobernación Departamental de Sacatepéquez',
  'Gobernación Departamental de Chimaltenango',
  'Gobernación Departamental de Escuintla',
  'Gobernación Departamental de Santa Rosa',
  'Gobernación Departamental de Sololá',
  'Gobernación Departamental de Totonicapán',
  'Gobernación Departamental de Quetzaltenango',
  'Gobernación Departamental de Suchitepéquez',
  'Gobernación Departamental de Retalhuleu',
  'Gobernación Departamental de San Marcos',
  'Gobernación Departamental de Huehuetenango',
  'Gobernación Departamental de Quiché',
  'Gobernación Departamental de Alta Verapaz',
  'Gobernación Departamental de Baja Verapaz',
  'Gobernación Departamental de Petén',
  'Gobernación Departamental de Izabal',
  'Gobernación Departamental de Zacapa',
  'Gobernación Departamental de Chiquimula',
  'Gobernación Departamental de Jalapa',
  'Gobernación Departamental de Jutiapa',
  'Otra (especificar)'
];

const Register = ({ editingPost, fetchPosts, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    date: '',
    author: '',
    network: 'X',
    mentioned: '',
    mentionedOther: '',
    persons: '',
    category: 'SEGUIMIENTO',
    desc: '',
    link: ''
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editingPost) {
      const isOtherInstitution = !INSTITUTIONS.slice(0, -1).includes(editingPost.mentioned);
      setFormData({
        date: (editingPost.date || '').slice(0, 10),
        author: editingPost.author || '',
        network: editingPost.network || 'X',
        mentioned: isOtherInstitution ? 'Otra (especificar)' : editingPost.mentioned || '',
        mentionedOther: isOtherInstitution ? editingPost.mentioned : '',
        persons: editingPost.persons || '',
        category: editingPost.category || 'SEGUIMIENTO',
        desc: editingPost.desc || '',
        link: editingPost.link || ''
      });
    } else {
      handleReset();
    }
  }, [editingPost]);

  const detectSocialNetwork = (url) => {
    if (!url) return 'X';
    
    const urlLower = url.toLowerCase();
    
    if (urlLower.includes('facebook.com') || urlLower.includes('fb.com')) {
      return 'Facebook';
    } else if (urlLower.includes('instagram.com')) {
      return 'Instagram';
    } else if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
      return 'YouTube';
    } else if (urlLower.includes('tiktok.com')) {
      return 'TikTok';
    } else if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) {
      return 'X';
    }
    
    return 'Web';
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-detectar red social cuando cambia el enlace
      if (field === 'link' && value) {
        newData.network = detectSocialNetwork(value);
      }
      
      return newData;
    });
    
    // Limpiar error del campo al editarlo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleReset = () => {
    setFormData({
      date: '',
      author: '',
      network: 'X',
      mentioned: '',
      mentionedOther: '',
      persons: '',
      category: 'SEGUIMIENTO',
      desc: '',
      link: ''
    });
    setErrors({});
    if (onCancel) onCancel();
  };

  const isValidURL = (url) => {
    try {
      const urlObj = new URL(url);
      return /^https?:$/.test(urlObj.protocol) && /\./.test(urlObj.hostname);
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date.trim()) {
      newErrors.date = 'La fecha es obligatoria';
    }

    if (!formData.author.trim()) {
      newErrors.author = 'El autor es obligatorio';
    }

    if (!formData.network) {
      newErrors.network = 'Seleccione la red social';
    }

    if (!formData.mentioned) {
      newErrors.mentioned = 'Seleccione la institución';
    } else if (formData.mentioned === 'Otra (especificar)' && !formData.mentionedOther.trim()) {
      newErrors.mentionedOther = 'Debe especificar la institución';
    }

    if (!formData.category) {
      newErrors.category = 'Seleccione la categoría';
    }

    if (!formData.desc.trim()) {
      newErrors.desc = 'La descripción es obligatoria';
    }

    if (!formData.link.trim()) {
      newErrors.link = 'El enlace es obligatorio';
    } else if (!isValidURL(formData.link)) {
      newErrors.link = 'Ingrese un enlace HTTP/HTTPS válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor corrija los campos marcados');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const postData = {
        date: new Date(formData.date + 'T00:00:00').toISOString(),
        author: formData.author.trim(),
        network: formData.network,
        mentioned: formData.mentioned === 'Otra (especificar)' 
          ? formData.mentionedOther.trim() 
          : formData.mentioned,
        persons: formData.persons.trim() || '',
        category: formData.category,
        desc: formData.desc.trim(),
        link: formData.link.trim()
      };

      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.id}`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Publicación actualizada correctamente');
      } else {
        await axios.post(`${API}/posts`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Publicación registrada correctamente');
      }

      await fetchPosts();
      handleReset();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar la publicación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-container fade-in" data-testid="register-view">
      <div className="container">
        <div className="panel">
          <div className="panel-header">
            <h2 className="panel-title">
              {editingPost ? 'Editar Publicación' : 'Registrar Nueva Publicación'}
            </h2>
            {editingPost && (
              <span className="editing-badge">Editando</span>
            )}
          </div>

          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-grid">
              {/* Fecha */}
              <div className="form-group">
                <label htmlFor="r-date">
                  Fecha del post <span className="required">*</span>
                </label>
                <input
                  id="r-date"
                  data-testid="input-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className={errors.date ? 'error' : ''}
                />
                {errors.date && (
                  <div className="error-message" data-testid="error-date">
                    <AlertCircle size={14} />
                    {errors.date}
                  </div>
                )}
              </div>

              {/* Autor */}
              <div className="form-group">
                <label htmlFor="r-author">
                  Autor <span className="required">*</span>
                </label>
                <input
                  id="r-author"
                  data-testid="input-author"
                  type="text"
                  placeholder="@usuario"
                  value={formData.author}
                  onChange={(e) => handleChange('author', e.target.value)}
                  className={errors.author ? 'error' : ''}
                />
                {errors.author && (
                  <div className="error-message" data-testid="error-author">
                    <AlertCircle size={14} />
                    {errors.author}
                  </div>
                )}
              </div>

              {/* Red Social */}
              <div className="form-group">
                <label htmlFor="r-network">
                  Red Social <span className="required">*</span>
                </label>
                <select
                  id="r-network"
                  data-testid="input-network"
                  value={formData.network}
                  onChange={(e) => handleChange('network', e.target.value)}
                  className={errors.network ? 'error' : ''}
                >
                  <option value="X">X</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Instagram">Instagram</option>
                  <option value="YouTube">YouTube</option>
                  <option value="TikTok">TikTok</option>
                  <option value="Web">Web</option>
                </select>
                {errors.network && (
                  <div className="error-message" data-testid="error-network">
                    <AlertCircle size={14} />
                    {errors.network}
                  </div>
                )}
              </div>

              {/* Institución */}
              <div className="form-group">
                <label htmlFor="r-mentioned">
                  Institución <span className="required">*</span>
                </label>
                <select
                  id="r-mentioned"
                  data-testid="input-mentioned"
                  value={formData.mentioned}
                  onChange={(e) => handleChange('mentioned', e.target.value)}
                  className={errors.mentioned ? 'error' : ''}
                >
                  <option value="">Seleccione...</option>
                  {INSTITUTIONS.map(inst => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </select>
                {errors.mentioned && (
                  <div className="error-message" data-testid="error-mentioned">
                    <AlertCircle size={14} />
                    {errors.mentioned}
                  </div>
                )}
              </div>

              {/* Otra Institución */}
              {formData.mentioned === 'Otra (especificar)' && (
                <div className="form-group">
                  <label htmlFor="r-mentioned-other">
                    Especifique la institución <span className="required">*</span>
                  </label>
                  <input
                    id="r-mentioned-other"
                    data-testid="input-mentioned-other"
                    type="text"
                    placeholder="Nombre de la institución"
                    value={formData.mentionedOther}
                    onChange={(e) => handleChange('mentionedOther', e.target.value)}
                    className={errors.mentionedOther ? 'error' : ''}
                  />
                  {errors.mentionedOther && (
                    <div className="error-message" data-testid="error-mentioned-other">
                      <AlertCircle size={14} />
                      {errors.mentionedOther}
                    </div>
                  )}
                </div>
              )}

              {/* Personas */}
              <div className="form-group">
                <label htmlFor="r-persons">Personas mencionadas (opcional)</label>
                <input
                  id="r-persons"
                  data-testid="input-persons"
                  type="text"
                  placeholder="N/A"
                  value={formData.persons}
                  onChange={(e) => handleChange('persons', e.target.value)}
                />
              </div>

              {/* Categoría */}
              <div className="form-group">
                <label htmlFor="r-category">
                  Categoría <span className="required">*</span>
                </label>
                <select
                  id="r-category"
                  data-testid="input-category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className={errors.category ? 'error' : ''}
                >
                  <option value="ATENDER">ATENDER</option>
                  <option value="SEGUIMIENTO">SEGUIMIENTO</option>
                  <option value="MONITOREO">MONITOREO</option>
                </select>
                {errors.category && (
                  <div className="error-message" data-testid="error-category">
                    <AlertCircle size={14} />
                    {errors.category}
                  </div>
                )}
              </div>
            </div>

            {/* Descripción */}
            <div className="form-group form-group-full">
              <label htmlFor="r-desc">
                Descripción <span className="required">*</span>
              </label>
              <textarea
                id="r-desc"
                data-testid="input-desc"
                placeholder="Describe brevemente la publicación..."
                value={formData.desc}
                onChange={(e) => handleChange('desc', e.target.value)}
                rows="4"
                className={errors.desc ? 'error' : ''}
              />
              {errors.desc && (
                <div className="error-message" data-testid="error-desc">
                  <AlertCircle size={14} />
                  {errors.desc}
                </div>
              )}
            </div>

            {/* Enlace */}
            <div className="form-group form-group-full">
              <label htmlFor="r-link">
                Enlace a la publicación <span className="required">*</span>
              </label>
              <input
                id="r-link"
                data-testid="input-link"
                type="url"
                placeholder="https://..."
                value={formData.link}
                onChange={(e) => handleChange('link', e.target.value)}
                className={errors.link ? 'error' : ''}
              />
              {errors.link && (
                <div className="error-message" data-testid="error-link">
                  <AlertCircle size={14} />
                  {errors.link}
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
                disabled={submitting}
                data-testid="reset-btn"
              >
                <X size={18} />
                {editingPost ? 'Cancelar' : 'Limpiar'}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
                data-testid="submit-btn"
              >
                <Save size={18} />
                {submitting ? 'Guardando...' : editingPost ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
